import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { useTranslation } from 'react-i18next';
import styles from './EditFolderDialog.module.scss';
import classNames from 'classnames/bind';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { IEditFolder } from '~/utils/interface/folder';
import Switch from '@mui/material/Switch';
import { jobActions } from '~/thunks/job/jobSlice';
import { handleCreateFolder, handleUpdateFolder } from '~/thunks/folder/folderThunk';

const cx = classNames.bind(styles);

type EditFolderDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  mode?: string;
  data?: string;
  id?: string;
};

const initialValues: IEditFolder = {
  name: '',
  targetId: '',
  targetType: 'jobs',
  isCurrent: true,
};

const EditFolderDialog = (props: EditFolderDialogProps) => {
  const { mode, data, id } = props;
  const dispatch = useAppDispatch();
  const { menuSelectedFolderId } = useAppSelector((state) => state.job);
  const { t } = useTranslation('admin/folder');
  const [checked, setChecked] = useState(true);

  useEffect(() => {
    form.setFieldValue('targetId', id);
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('name', cast(data));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);

  const handleCancel = () => {
    handleSetOpen(false);
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateFolder(values)).unwrap();
          handleSetOpen(false);
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          dispatch(jobActions.changeNeedRefreshData(true));
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(handleUpdateFolder({ id: Number(menuSelectedFolderId), params: values })).unwrap();
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          dispatch(jobActions.changeNeedRefreshData(true));
          handleSetOpen(false);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('add') : t('edit')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box className={`mb-[30px] ${cx('name')}`}>
          <InputLabel className='mb-[20px]'>{t('editFolderDialog.name')}</InputLabel>
          <TextField
            name='name'
            placeholder={t('editFolderDialog.name')}
            value={form.values.name}
            onChange={form.handleChange}
            error={Boolean(form.errors?.name)}
            helperText={form.errors?.name}
          />
        </Box>

        <Box className={cx('header')}>
          <Typography className='mr-[10px]'>{t('editFolderDialog.applyToProject')}</Typography>
          <Switch
            name='isCurrent'
            defaultChecked
            onClick={() => {
              setChecked(!checked);
              form.setFieldValue('isCurrent', checked);
            }}
            value={form.values.isCurrent}
            onChange={form.handleChange}
          />
        </Box>
      </DialogContent>

      <DialogActions className='justify-end'>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditFolderDialog);
